import type { SubmissionOption } from './sampleReview';

/**
 * How a submission reads once options exist.
 *
 * The grouping is pure so the states that matter can be proved without a
 * browser: a slot with a winner is locked, a slot whose options were all
 * denied needs another round, and a submission is only finished when every
 * slot has a winner.
 */

export type SlotState = 'locked' | 'revision_required' | 'awaiting_review' | 'collecting' | 'empty';

export interface ComponentSlot {
  component: string;
  /** Only the newest round. Earlier rounds are history, shown as context. */
  options: SubmissionOption[];
  previousRounds: SubmissionOption[];
  winner: SubmissionOption | null;
  state: SlotState;
  round: number;
}

export function buildSlots(components: string[], options: SubmissionOption[]): ComponentSlot[] {
  return components.map((component) => {
    const mine = options.filter((option) => option.component_type === component);

    // A winner can live in an earlier round: approving option 2 of round 1
    // locks the slot, and a later round should never have been uploaded.
    const winner = mine.find((option) => option.verdict === 'approved') ?? null;
    const round = mine.reduce((highest, option) => Math.max(highest, option.round), 1);
    const current = mine.filter((option) => option.round === round);
    const previousRounds = mine.filter((option) => option.round !== round);

    return {
      component,
      options: current,
      previousRounds,
      winner,
      round,
      state: slotState(current, winner),
    };
  });
}

function slotState(current: SubmissionOption[], winner: SubmissionOption | null): SlotState {
  if (winner) return 'locked';
  if (current.length === 0) return 'empty';
  // Every option judged and none approved: the supplier has to try again.
  // "Some still pending" is NOT this state — it is work the reviewer owes.
  if (current.every((option) => option.verdict === 'denied')) {
    return 'revision_required';
  }
  // Still being assembled: something in this round has not been SENT.
  //
  // Kyu: "it does not let the factory upload more than 1 per item" — and then
  // the rule: "Supplier should send them in batches, once they submitted it
  // they would have to wait til we review."
  //
  // Uploading and submitting used to be the same event, so the first photo
  // put the component with TAP and closed it. There was no state in which a
  // factory was holding a set they had not yet offered, which is exactly the
  // state a batch needs. submitted_at (20261119000200) is that state, and it
  // is the ONLY thing that decides this — deliberately not "has TAP judged
  // anything", which would let a factory keep adding to a set already sent.
  if (current.some((option) => option.submitted_at === null)) {
    return 'collecting';
  }
  return 'awaiting_review';
}

/**
 * Components the supplier may upload into.
 *
 * Empty (nothing sent yet), revision_required (everything denied, try again),
 * and collecting (uploaded, nothing judged yet — the factory is still
 * choosing what to show).
 *
 * Deliberately NOT awaiting_review, which now means something narrower than
 * it did: TAP has ruled on at least one option in this round and not on the
 * rest. Adding to a set somebody is halfway through judging moves the target
 * — the reviewer comes back to options they did not start on, with the
 * five-per-round cap quietly consumed. That reasoning was always right; what
 * was wrong was applying it from the moment the first photo landed, which
 * closed the slot before TAP had looked at anything.
 *
 * A locked slot never reopens. That one is also enforced in the database by
 * guard_option_insert (migration 20260919000100), because this function is
 * only a statement about what the interface offers.
 */
export function openComponents(slots: ComponentSlot[]): string[] {
  return slots
    .filter(
      (slot) =>
        slot.state === 'empty' || slot.state === 'revision_required' || slot.state === 'collecting',
    )
    .map((slot) => slot.component);
}

/**
 * The final gate: every component has a locked winner.
 *
 * Deliberately requires the component list, not just the options. A submission
 * with three perfect components and nothing uploaded for the fourth is not
 * approved, and counting only what exists would call it done.
 */
export function isFullyApproved(slots: ComponentSlot[]): boolean {
  return slots.length > 0 && slots.every((slot) => slot.state === 'locked');
}

/**
 * Five options per component, per round. Mirrors submission_options_limit in
 * 20260918000100 — duplicated here to label a button and to stop offering one
 * that can only fail. The database is still what enforces it.
 */
export const OPTIONS_PER_ROUND = 5;

/**
 * The upload button's text.
 *
 * Numbered rather than named ("+ Upload Option 2 Photo", not "Upload Charm
 * photo") because the old label gave no hint that a component takes several
 * photos to choose between — suppliers sent one, and TAP had nothing to pick
 * from. The number counts the CURRENT round only, which is what the cap
 * counts, so after a revision it starts again at 1.
 */
export function optionButtonLabel(currentRoundCount: number): string {
  return `+ Upload Option ${String(currentRoundCount + 1)} Photo`;
}

/** Whether this round has used up its five options. */
export function isRoundFull(currentRoundCount: number): boolean {
  return currentRoundCount >= OPTIONS_PER_ROUND;
}

/** Feedback the supplier should see when resubmitting a component. */
export function rejectionNotes(slot: ComponentSlot): { label: string; feedback: string }[] {
  return [...slot.previousRounds, ...slot.options]
    .filter((option) => option.verdict === 'denied' && option.feedback.trim() !== '')
    .map((option, index) => ({
      label: option.label.trim() === '' ? `Option ${String(index + 1)}` : option.label,
      feedback: option.feedback,
    }));
}
